/**
 * Shared property-provider resolution.
 *
 * A `{name}` binding is provided by the nearest Properties behavior on the
 * object OR one of its ancestors that DECLARES `name`. Several editor modules
 * need this walk (`renderer-bindings`, `statesActions`, `material-variants`);
 * it lives here rather than in any one of them because `renderer-bindings`
 * already imports `statesActions`, so `statesActions` importing back into
 * `renderer-bindings` would be an `import/no-cycle` violation.
 */

import type { SceneObject } from '../core/SceneObject';
import { Properties } from '../behaviors/properties';

/**
 * Walk `obj` and its ancestors for the Properties behavior that DECLARES
 * `name` — the binding's provider. Returns the owning object AND behavior
 * (the undo command needs the object; `hasProperty` alone loses it).
 */
export function findPropertyProvider(
    obj: SceneObject,
    name: string,
): { owner: SceneObject; properties: Properties } | null {
    let owner: SceneObject | null = obj;
    while (owner) {
        const p = owner.getBehavior(Properties);
        if (p?.hasProperty(name)) return { owner, properties: p };
        owner = owner.getParent();
    }
    return null;
}
